import React, { useState, useRef } from "react";
import { Grid, Button } from "@material-ui/core";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import axios from "axios";
import { getGoogleAddressFields } from "../../shared/services/SharedService";
import "../AddNewPhase.css";

const AddNewPhasePage = ({ incStepCounter, decStepCounter, addPhase }) => {
  window.onscroll = function () {
    effects();
  };
  function effects() {}

  const { project_id } = useParams();
  const getPersDetails = async () => {
    try {
      const { data } = await axios.get(
        `${global.REACT_APP_API_ENDPOINT}/phase/getProjectAddress/${project_id}`
      );
      setPhaseInfo({
        ...phaseInfo,
        street: data.street || "",
        city: data.city || "",
        state: data.state || "",
        zip5: data.zip5 || "",
        phaseType: "Legal", // Set default phase type
      });
    } catch (err) {
      console.log(err);
    }
  };

  const [phaseInfo, setPhaseInfo] = useState({
    phaseName: "",
    phaseType: "",
    buildingNumber: "",
    unitNumber: "",
    unitsPerPhase: "",
    street: "",
    city: "",
    state: "",
    zip5: "",
    comment: "",
  });

  React.useEffect(() => {
    getPersDetails();
  }, []);

  // Initialize Google Autocomplete when component mounts and when phase info is loaded
  React.useEffect(() => {
    if (window.google && window.google.maps && phaseInfo.city && phaseInfo.state && phaseInfo.zip5) {
      // Small delay to ensure the input is properly mounted
      setTimeout(() => {
        initializeAutoComplete();
      }, 100);
    }
  }, [phaseInfo.city, phaseInfo.state, phaseInfo.zip5]);

  // Error states
  const [errPhaseName, setErrPhaseName] = useState("");
  const [errPhaseType, setErrPhaseType] = useState("");
  const [errBuildingNumber, setErrBuildingNumber] = useState("");
  const [errUnitNumber, setErrUnitNumber] = useState("");
  const [errUnitsPerPhase, setErrUnitsPerPhase] = useState("");
  const [errAddress, setErrAddress] = useState("");
  const [commentLength, setCommentLength] = useState(0);

  const phaseNameRef = React.createRef();
  const phaseTypeRef = React.createRef();
  const unitNumberRef = React.createRef();
  const buildingNumberRef = React.createRef();
  const unitsPerPhaseRef = React.createRef();
  const [disable, setDisable] = useState(false);
  const dispatch = useDispatch();

  // Google Autocomplete ref
  const streetAddressRef = useRef(null);

  // Function to update only street address, preserve city/state/zip
  const updateStreetAddress = (place) => {
    const address = getGoogleAddressFields(place);
    
    if (address.street) {
      setPhaseInfo(prev => ({
        ...prev,
        street: address.street
      }));
      setErrAddress(""); // Clear validation errors
    }
  };

  // Initialize Google Autocomplete with geographic restrictions
  const initializeAutoComplete = () => {
    if (!streetAddressRef?.current || 
        !window.google?.maps?.places ||
        !phaseInfo.city || !phaseInfo.state || !phaseInfo.zip5) {
      return;
    }

    try {
      const input = streetAddressRef.current;
      const autocomplete = new window.google.maps.places.Autocomplete(input, {
        componentRestrictions: { country: 'US' },
        fields: ["address_components", "geometry"],
        types: ['address']
      });

      // Set geographic bounds for local suggestions
      setGeographicBounds(autocomplete);

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place && isValidAddressForLocation(place)) {
          updateStreetAddress(place);
        }
      });
    } catch (error) {
      console.error('Error initializing autocomplete:', error);
    }
  };

  // Set geographic bounds based on current city/state/zip
  const setGeographicBounds = (autocomplete) => {
    const geocoder = new window.google.maps.Geocoder();
    const searchAddress = `${phaseInfo.city}, ${phaseInfo.state} ${phaseInfo.zip5}, USA`;
    
    geocoder.geocode({ address: searchAddress }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        const bounds = new window.google.maps.LatLngBounds();
        
        const offset = 0.01; // ~1km radius
        bounds.extend(new window.google.maps.LatLng(
          location.lat() - offset, location.lng() - offset
        ));
        bounds.extend(new window.google.maps.LatLng(
          location.lat() + offset, location.lng() + offset
        ));
        
        autocomplete.setBounds(bounds);
      }
    });
  };

  // Validate selected address matches required location
  const isValidAddressForLocation = (place) => {
    const selectedAddress = getGoogleAddressFields(place);
    
    if (!selectedAddress.city || !selectedAddress.state || !selectedAddress.zip) {
      return false;
    }

    const isValidLocation = 
      selectedAddress.city.toLowerCase() === phaseInfo.city.toLowerCase() && 
      selectedAddress.state.toLowerCase() === phaseInfo.state.toLowerCase() && 
      selectedAddress.zip === phaseInfo.zip5;

    if (!isValidLocation) {
      setErrAddress("The selected address does not match the city, state, or zip of the project.");
      // Clear the input
      if (streetAddressRef.current) {
        streetAddressRef.current.value = '';
      }
      setPhaseInfo(prev => ({
        ...prev,
        street: ''
      }));
    }

    return isValidLocation;
  };

  const states = useSelector((store) => store.dialogueReducer.states);
  const phaseTypes = useSelector(
    (store) => store.addNewPhaseReducer.phaseTypes
  );

  function handleChange(event) {
    setPhaseInfo({ ...phaseInfo, [event.target.name]: event.target.value });
  }
  async function onSubmitCalled(e) {
    console.log("Add button clicked, project_id:", project_id);
    e.preventDefault();

    // Reset disable state initially
    setDisable(false);

    if (
      phaseNameRef.current.value != "" &&
      phaseNameRef.current.value.length > 300
    ) {
      setErrPhaseName("Phase Name cannot be larger than 300 characters");
    } else {
      setErrPhaseName(getRequiredErrorMsg(phaseNameRef));
    }

    if (
      buildingNumberRef.current.value != "" &&
      buildingNumberRef.current.value.length > 100
    ) {
      setErrBuildingNumber(
        "Building Number cannot be larger than 100 characters"
      );
    }

    if (
      unitNumberRef.current.value != "" &&
      unitNumberRef.current.value.length > 100
    ) {
      setErrUnitNumber("Unit Number cannot be larger than 100 characters");
    }

    setErrPhaseType(getRequiredErrorMsg(phaseTypeRef));
    setErrAddress(streetAddressRef.current?.value ? "" : "This field is required");
    if (
      unitsPerPhaseRef.current.value != "" &&
      unitsPerPhaseRef.current.value <= 0
    ) {
      setErrUnitsPerPhase("Number of phases cannot be 0 or negative");
    } else {
      setErrUnitsPerPhase(getRequiredErrorMsg(unitsPerPhaseRef));
    }

    // Check if there are any validation errors
    const hasErrors = 
      isValueEmpty(phaseNameRef) ||
      isValueEmpty(phaseTypeRef) ||
      isValueEmpty(unitsPerPhaseRef) ||
      !streetAddressRef.current?.value ||
      phaseNameRef.current.value.length > 300 ||
      buildingNumberRef.current.value.length > 100 ||
      unitNumberRef.current.value.length > 100 ||
      unitsPerPhaseRef.current.value <= 0;

    console.log("Validation check:", {
      hasErrors,
      phaseName: phaseNameRef.current?.value,
      phaseType: phaseTypeRef.current?.value,
      unitsPerPhase: unitsPerPhaseRef.current?.value,
      streetAddress: streetAddressRef.current?.value,
      phaseInfo
    });

    if (!hasErrors) {
      // Only disable button when actually submitting
      setDisable(true);
      try {
        const newPhase = await submitNewPhase();
        addPhase(newPhase.data);
        incStepCounter();
      } catch (err) {
        console.error("Error submitting phase:", err);
        // Re-enable button if there's an error
        setDisable(false);
      }
    }
  }

  async function submitNewPhase() {
    const passData = JSON.parse(sessionStorage.getItem("passData"));

    const formData = {
      projectId: project_id,
      userName: passData.user,
      phaseName: phaseInfo.phaseName,
      cpmId: phaseInfo.cpmId,
      phaseType: phaseInfo.phaseType,
      buildingNumber: phaseInfo.buildingNumber,
      unitNumber: phaseInfo.unitNumber,
      unitCount: phaseInfo.unitsPerPhase,
      street: phaseInfo.street,
      city: phaseInfo.city,
      state: phaseInfo.state,
      zip5: phaseInfo.zip5,
      comments: phaseInfo.comment,
    };

    try {
      return await axios.post(
        global.REACT_APP_API_ENDPOINT + `/phase/addNewPhase`,
        formData
      );
    } catch (err) {
      throw Error(err);
    }
  }

  const backButtonClicked = (e) => {
    decStepCounter();
  };

  const isValueEmpty = (ref) => ref.current.value.length === 0;

  const getRequiredErrorMsg = (ref) =>
    isValueEmpty(ref) ? "This field is required" : "";

  return (
    <Grid item xs={12}>
      <form id="addPhaseForm">
        <Grid item xs={12}>
          <h1 style={{ justifyContent: "center" }}>Add New Phase</h1>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2} className="dialoguClass">
            <Grid item xs={12} sm={12} md={12}>
              <div>
                Phase Name<span style={{ color: "red" }}>*</span>
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={7}>
              <input
                required
                type="text"
                className="form-control"
                name="phaseName"
                value={phaseInfo.phaseName}
                ref={phaseNameRef}
                onChange={(event) => {
                  handleChange(event);
                  setErrPhaseName(getRequiredErrorMsg(phaseNameRef));
                  if (event.target.value.length > 300) {
                    setErrPhaseName(
                      "Phase Name cannot be larger than 300 characters"
                    );
                  } else {
                    setErrPhaseName("");
                  }
                }}
              />
              {errPhaseName != "" ? (
                <p style={{ color: "red", margin: "0px" }}>{errPhaseName}</p>
              ) : null}
            </Grid>
          </Grid>
          <Grid container spacing={2} className="dialoguClass">
            <Grid item xs={12} sm={12} md={12}>
              <div>Legacy CPM ID</div>
            </Grid>
            <Grid item xs={12} sm={12} md={7}>
              <input
                type="text"
                className="form-control"
                name="cpmId"
                value={phaseInfo.cpmId}
                maxLength="16"
                onChange={(event) => {
                  handleChange(event);
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="dialoguClass">
            <Grid item xs={12} sm={12} md={12}>
              <div>
                Phase Type<span style={{ color: "red" }}>*</span>
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={7}>
              <select
                required
                name="phaseType"
                className="form-control"
                ref={phaseTypeRef}
                value={phaseInfo.phaseType}
                onChange={(event) => {
                  handleChange(event);
                  setErrPhaseType(getRequiredErrorMsg(phaseTypeRef));
                }}
              >
                <option value="">Select Phase Type</option>
                {phaseTypes &&
                  phaseTypes.length > 0 &&
                  phaseTypes.map((item) => {
                    return (
                      <option key={item.key} value={item.key}>
                        {item.value}
                      </option>
                    );
                  })}
              </select>
              {errPhaseType != "" ? (
                <p style={{ color: "red", margin: "0px" }}>{errPhaseType}</p>
              ) : null}
            </Grid>
          </Grid>
          <Grid container spacing={2} className="dialoguClass">
            <Grid item xs={12} sm={12} md={12}>
              <div>Building Number</div>
            </Grid>
            <Grid item xs={12} sm={12} md={7}>
              <input
                type="text"
                className="form-control"
                name="buildingNumber"
                ref={buildingNumberRef}
                value={phaseInfo.buildingNumber}
                onChange={(event) => {
                  handleChange(event);
                }}
              />
              {errBuildingNumber != "" ? (
                <p style={{ color: "red", margin: "0px" }}>
                  {errBuildingNumber}
                </p>
              ) : null}
            </Grid>
          </Grid>
          <Grid container spacing={2} className="dialoguClass">
            <Grid item xs={12} sm={12} md={12}>
              <div>Unit Number</div>
            </Grid>
            <Grid item xs={12} sm={12} md={7}>
              <input
                type="text"
                className="form-control"
                name="unitNumber"
                ref={unitNumberRef}
                value={phaseInfo.unitNumber}
                onChange={(event) => {
                  handleChange(event);
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="dialoguClass">
            <Grid item xs={12} sm={12} md={12}>
              <div>
                Number of Units in Phase<span style={{ color: "red" }}>*</span>
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={7}>
              <input
                required
                type="number"
                min="1"
                className="form-control"
                name="unitsPerPhase"
                value={phaseInfo.unitsPerPhase}
                ref={unitsPerPhaseRef}
                onChange={(event) => {
                  handleChange(event);
                  setErrUnitsPerPhase(getRequiredErrorMsg(unitsPerPhaseRef));
                  if (event.target.value <= 0) {
                    setErrUnitsPerPhase(
                      "Number of phases cannot be 0 or negative"
                    );
                  } else {
                    setErrUnitsPerPhase("");
                  }
                }}
              />
              {errUnitsPerPhase != "" ? (
                <p style={{ color: "red", margin: "0px" }}>
                  {errUnitsPerPhase}
                </p>
              ) : null}
            </Grid>
          </Grid>
          <Grid container spacing={2} className="dialoguClass">
            <Grid item xs={12} sm={12} md={12}>
              <div>
                Phase Address<span style={{ color: "red" }}>*</span>
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={7}>
              <input
                required
                type="text"
                className="form-control"
                name="street"
                value={phaseInfo.street}
                ref={streetAddressRef}
                placeholder=""
                onChange={(event) => {
                  handleChange(event);
                  setErrAddress(streetAddressRef.current?.value ? "" : "This field is required");
                }}
              />
              {errAddress != "" ? (
                <p style={{ color: "red", margin: "0px" }}>{errAddress}</p>
              ) : null}
            </Grid>
          </Grid>
          <Grid container spacing={2} className="dialoguClass">
            <Grid item xs={12} sm={12} md={12}>
              <div>
                City<span style={{ color: "red" }}>*</span>
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={7}>
              <p>{phaseInfo.city}</p>
            </Grid>
          </Grid>
          <Grid container spacing={2} className="dialoguClass">
            <Grid item xs={12} sm={12} md={12}>
              <div>
                State<span style={{ color: "red" }}>*</span>
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={7}>
              <p>{phaseInfo.state}</p>
            </Grid>
          </Grid>
          <Grid container spacing={2} className="dialoguClass">
            <Grid item xs={12} sm={12} md={12}>
              <div>
                Zip<span style={{ color: "red" }}>*</span>
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={7}>
              <p>{phaseInfo.zip5}</p>
            </Grid>
          </Grid>
          <Grid container spacing={2} className="dialoguClass">
            <Grid item xs={12} sm={12} md={12}>
              <div>Comment</div>
            </Grid>
            <Grid item xs={12} sm={12} md={7}>
              <div>
                <textarea
                  rows="3"
                  type="text"
                  maxLength="5000"
                  className="form-control mt-3"
                  name="comment"
                  placeholder="Enter your Comment"
                  onChange={(e) => {
                    handleChange(e);
                    setCommentLength(e.target.value.length);
                  }}
                ></textarea>
                <span
                  style={{
                    display: "inline-block",
                    marginRight: "5px",
                    float: "right",
                    fontSize: "14px",
                    color: "#787979",
                  }}
                >{`${commentLength}/5000`}</span>
              </div>
            </Grid>
          </Grid>
        </Grid>
        <Grid container className="dialoguClass">
          <Grid item xs={12} sm={12} md={12} style={{ padding: "8px" }}>
            <Button
              type="button"
              disabled={disable}
              style={{
                color: "white",
                backgroundColor: "#1176B5",
                borderRadius: "4px",
                boxShadow:
                  "0 0.4px 0.6px 0 rgba(0,0,0,0.37), 0 0.1px 0 0.4px #0A649D",
                height: "40px",
                width: "150px",
                textTransform: "capitalize",
              }}
              onClick={(e) => onSubmitCalled(e)}
            >
              Add
            </Button>
            <Button
              type="submit"
              className="ml-3"
              style={{
                color: "black",
                backgroundColor: "#fcfcfc",
                borderRadius: "4px",
                boxShadow:
                  "0 0.4px 0.6px 0 rgba(0,0,0,0.37), 0 0.1px 0 0.4px #0A649D",
                height: "40px",
                width: "150px",
                textTransform: "capitalize",
              }}
              form="addPhaseForm"
              href={`/project/${project_id}`}
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      </form>
    </Grid>
  );
};

export default AddNewPhasePage;
